import {
  Organisation,
  OrganisationFilterRequest,
  OrganisationPost,
  OrganisationPut,
  ProductTable,
  RatingTable,
} from "callable-data-objects";
import { GoogleASRLanguage } from "callable-data-objects/dist/enums/GoogleASRLanguage";
import { GoogleTTSLanguage } from "callable-data-objects/dist/enums/GoogleTTSLanguage";
import Page from "callable-data-objects/dist/services/Page";
import { APIOrganisationContext } from "Providers/APIContexts";
import { APIOrganisationProps, DefaultProviderProps } from "Providers/types";
import React, { useState } from "react";
import { useAPI } from "./APIProvider";

const APIOrganisationProvider = ({ children }: DefaultProviderProps) => {
  const api = useAPI();
  const [organisations, setOrganisations] = useState<
    Page<Organisation> | undefined
  >();
  const [listOrganisations, setListOrganisations] = useState<Organisation[]>(
    []
  );
  const [organisation, setOrganisation] = useState<Organisation | undefined>();
  const [create, setCreate] = useState<OrganisationPost | undefined>();
  const [filter, setFilter] = useState<OrganisationFilterRequest>({
    name: "",
    page: 0,
    max: 12,
    sortField: "name",
    sort: "ASC",
    projections: ["id", "name", "customers"],
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [productTables, setProductTables] = useState<ProductTable[]>([]);
  const [ratingTables, setRatingTables] = useState<RatingTable[]>([]);
  const [xeroURL, setXeroURL] = useState<String | undefined>();
  const [xeroAuthorised, setXeroAuthorised] = useState<boolean>(false);

  React.useEffect(() => {
    if (filter) {
      api.organisationService.list().then((resp: Organisation[]) => {
        setListOrganisations(resp);
      });
      api.productTableService.list().then((resp) => setProductTables(resp));
      api.ratingTableService.list().then((resp) => setRatingTables(resp));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (filter) {
      api.organisationService
        .filter(filter)
        .then((resp: Page<Organisation>) => {
          setOrganisations(resp);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  function search(name: string): void {
    setFilter({
      ...filter,
      name,
      page: 0,
    });
  }

  function select(id: string): void {
    api.organisationService.get(id).then((resp) => {
      setOrganisation(resp);
      if (resp.cdrSettings && resp.cdrSettings.type === "xero") {
        isAuthorised();
      }
    });
  }

  function unselect() {
    setOrganisation(undefined);
  }

  function createOrganisation() {
    setCreate({
      name: "",
      originationRates: { id: "", name: "" },
      terminationRates: { id: "", name: "" },
      productTable: { id: "", name: "" },
      timeZone: "Europe/London",
      ttsLanguage: GoogleTTSLanguage.English_UK_Standard_C_Female,
      asrLanguage: GoogleASRLanguage.English_United_Kingdom,
    });
  }

  function uncreate() {
    setCreate(undefined);
  }

  function validateName(value: string): string | undefined {
    let error: string | undefined;
    if (value === null || value === "") error = "Name required";
    if (value.length < 3) error = "Name must be 3 or more characters";
    if (
      listOrganisations &&
      listOrganisations
        .filter((org) => !organisation || organisation.id !== org.id)
        .map((org) => org.name)
        .indexOf(value) > -1
    ) {
      error = "Name not unique";
    }
    return error;
  }

  async function save(payload: OrganisationPost) {
    if (create) {
      setLoading(true);
      const response = await api.organisationService.post("", payload);
      if (response) {
        select(response.id);
      }
      search("");
      uncreate();
      setLoading(false);
    }
  }

  async function update(payload: OrganisationPut) {
    if (organisation) {
      setLoading(true);
      await api.organisationService.put(organisation.id, payload);
      select(organisation.id);
      search("");
      setLoading(false);
    }
  }

  async function remove(id: string) {
    setLoading(true);
    await api.organisationService.delete(id);
    search("");
    unselect();
    setLoading(false);
  }

  async function addRole(roleId: string) {
    if (organisation) {
      setLoading(true);
      console.log("Add role", organisation);
      const response = await api.organisationService.addRole(
        organisation.id,
        roleId
      );
      if (response) setOrganisation(response);
      setLoading(false);
    }
  }

  async function removeRole(roleId: string) {
    if (organisation) {
      console.log("Remove role", organisation);
      setLoading(true);
      const response = await api.organisationService.removeRole(
        organisation.id,
        roleId
      );
      if (response) setOrganisation(response);
      setLoading(false);
    }
  }

  function authorise() {
    if (organisation) {
      api.xeroService.authorise(organisation.id).then((url: String) => {
        setXeroURL(url);
      });
    }
  }

  function isAuthorised() {
    if (organisation) {
      api.xeroService.isAuthorised(organisation.id).then(() => {
        setXeroAuthorised(true);
      });
    }
  }

  const orgProps: APIOrganisationProps = {
    organisations,
    organisation,
    listOrganisations,
    filter,
    setFilter,
    create,
    save,
    update,
    validateName,
    search,
    select,
    unselect,
    createOrganisation,
    uncreate,
    loading,
    productTables,
    ratingTables,
    remove,
    addRole,
    removeRole,
    xeroURL,
    authorise,
    xeroAuthorised,
  };

  return (
    <APIOrganisationContext.Provider value={orgProps}>
      {children}
    </APIOrganisationContext.Provider>
  );
};

export default APIOrganisationProvider;
